import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { Box, Typography, Chip, IconButton, Tooltip, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { GoogleMap, useJsApiLoader, Marker, Polyline, Circle } from '@react-google-maps/api';
import { useSelector } from 'react-redux';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import PersonIcon from '@material-ui/icons/Person';
import RoomIcon from '@material-ui/icons/Room';
import FlagIcon from '@material-ui/icons/Flag';
import SpeedIcon from '@material-ui/icons/Speed';
import { otLabel, otTooltip } from '../../utils/otLabel';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import WarningIcon from '@material-ui/icons/Warning';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import HelpIcon from '@material-ui/icons/Help';
import ChatBubbleIcon from '@material-ui/icons/ChatBubble';
import CenterFocusStrongIcon from '@material-ui/icons/CenterFocusStrong';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import CloseIcon from '@material-ui/icons/Close';
import { selectVehicles } from '../../store/slices/vehiclesSlice';

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
const LIBRARIES = ['places'];

const MAP_OPTIONS = {
  disableDefaultUI: true,
  zoomControl: true,
  zoomControlOptions: { position: 9 },
  clickableIcons: false,
  gestureHandling: 'greedy',
  mapTypeId: 'roadmap',
  styles: [
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  ],
};

const useStyles = makeStyles(() => ({
  root: {
    flex: 1, display: 'flex', flexDirection: 'column',
    background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
    overflow: 'hidden', margin: '0 24px 16px',
  },
  toolbar: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '10px 16px', background: '#f8fafc',
    borderBottom: '1px solid #e2e8f0', flexShrink: 0,
  },
  toolbarLabel: {
    fontSize: '0.58rem', fontWeight: 800, color: '#64748b',
    textTransform: 'uppercase', letterSpacing: '0.05em',
  },
  // ───── urgent strip ─────
  urgentStrip: {
    display: 'flex', gap: 8, overflowX: 'auto',
    padding: '10px 16px', background: 'linear-gradient(180deg, #fef2f2, #fff)',
    borderBottom: '1px solid #fecaca', flexShrink: 0,
  },
  urgentChip: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '6px 12px', borderRadius: 10,
    background: '#fff', border: '1.5px solid #fecaca',
    cursor: 'pointer', transition: 'all 0.15s',
    minWidth: 180, flexShrink: 0,
    '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(239,68,68,0.15)' },
  },
  // ───── split ─────
  split: {
    flex: 1, display: 'flex', minHeight: 0,
  },
  leftPane: {
    width: 420, flexShrink: 0,
    display: 'flex', flexDirection: 'column',
    borderRight: '1px solid #e2e8f0', background: '#fafbfc',
  },
  listHeader: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '10px 14px', background: '#f1f5f9',
    borderBottom: '1px solid #e2e8f0',
  },
  list: {
    flex: 1, overflowY: 'auto',
  },
  card: {
    padding: '10px 14px',
    borderBottom: '1px solid #f1f5f9',
    cursor: 'pointer',
    transition: 'background 0.12s',
    '&:hover': { background: '#f8fafc' },
  },
  cardSelected: {
    background: 'rgba(99,102,241,0.08) !important',
    borderLeft: '3px solid #6366f1',
  },
  cardHeader: {
    display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4,
  },
  healthDot: {
    width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
    boxShadow: '0 0 0 2px #fff, 0 0 0 3px rgba(0,0,0,0.05)',
  },
  plate: {
    fontSize: '0.78rem', fontWeight: 800, color: '#0f172a',
  },
  orderNum: {
    fontSize: '0.6rem', color: '#64748b', fontWeight: 600,
  },
  cardRow: {
    display: 'flex', alignItems: 'center', gap: 6, marginTop: 3,
    fontSize: '0.62rem', color: '#64748b',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  etaBadge: {
    display: 'inline-flex', alignItems: 'center', gap: 3,
    padding: '2px 7px', borderRadius: 6,
    fontSize: '0.58rem', fontWeight: 800,
  },
  // ───── right pane ─────
  rightPane: {
    flex: 1, minWidth: 0, position: 'relative',
  },
  mapInfoBar: {
    position: 'absolute', top: 12, left: 12, right: 12,
    padding: '10px 16px',
    background: 'rgba(15,23,42,0.65)',
    backdropFilter: 'blur(18px) saturate(160%)',
    WebkitBackdropFilter: 'blur(18px) saturate(160%)',
    border: '1px solid rgba(255,255,255,0.18)',
    borderRadius: 12,
    boxShadow: '0 10px 30px -8px rgba(0,0,0,0.3)',
    zIndex: 5,
    color: '#fff',
    display: 'flex', alignItems: 'center', gap: 10,
    pointerEvents: 'none',
  },
  emptyState: {
    flex: 1, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    gap: 10, padding: 32, color: '#94a3b8',
  },
  legend: {
    display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center',
  },
  legendItem: {
    display: 'flex', alignItems: 'center', gap: 4,
    fontSize: '0.58rem', fontWeight: 700, color: '#64748b',
  },
}));

// ─── Health calculation ───
// Returns one of: 'green' | 'yellow' | 'red' | 'grey'
function computeHealth(order) {
  if (!order.estimatedArrival || !order.deliveryWindowTo) return 'grey';
  const eta = new Date(order.estimatedArrival).getTime();
  const winEnd = new Date(order.deliveryWindowTo).getTime();
  const diffMin = (eta - winEnd) / 60000;
  if (diffMin > 10) return 'red';     // will miss window by > 10 min
  if (diffMin > -30) return 'yellow'; // within 30 min risk
  return 'green';
}

const HEALTH_CFG = {
  green:  { color: '#22c55e', label: 'No prazo', icon: CheckCircleIcon },
  yellow: { color: '#f59e0b', label: 'Em risco', icon: AccessTimeIcon },
  red:    { color: '#ef4444', label: 'Atrasada', icon: WarningIcon },
  grey:   { color: '#94a3b8', label: 'Sem dados', icon: HelpIcon },
};

function formatTimeDiffMin(min) {
  if (min == null) return '';
  const abs = Math.abs(Math.round(min));
  if (abs < 60) return `${abs}m`;
  return `${Math.floor(abs / 60)}h${abs % 60 > 0 ? (abs % 60) + 'm' : ''}`;
}

function OrdersLiveView({ orders, onSelectOrder, onContactDriver, lang = 'pt' }) {
  const classes = useStyles();
  const vehicles = useSelector(selectVehicles);
  const mapRef = useRef(null);
  const [selectedId, setSelectedId] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_KEY,
    libraries: LIBRARIES,
    language: 'pt',
    region: 'PT',
  });

  // Active orders — broader than just in_transit so the live dashboard
  // surfaces the full operational picture: orders waiting to start (assigned
  // with route), in execution (in_transit), and recently completed (delivered
  // in the last 24h). The per-OT live detail lives in LiveRoutePanel; here
  // we only show summary cards and a map overview.
  const activeOrders = useMemo(() => {
    const cutoff = Date.now() - 24 * 3600 * 1000; // 24h window for delivered
    return orders
      .filter(o => {
        if (o.status === 'in_transit') return true;
        if (o.status === 'assigned' && o.assignedRouteId) return true;
        if (o.status === 'delivered') {
          const d = o.deliveredAt ? new Date(o.deliveredAt).getTime() : 0;
          return d >= cutoff;
        }
        return false;
      })
      .map(o => {
        const v = vehicles.find(x => x.id === o.vehicleId || String(x.id) === String(o.vehicleId));
        const health = computeHealth(o);
        // Delay vs window end (minutes, signed)
        let delayMin = null;
        if (o.estimatedArrival && o.deliveryWindowTo) {
          delayMin = Math.round(
            (new Date(o.estimatedArrival).getTime() - new Date(o.deliveryWindowTo).getTime()) / 60000
          );
        }
        return { ...o, _vehicle: v, _health: health, _delayMin: delayMin };
      })
      .sort((a, b) => {
        // Sort by health severity: red → yellow → grey → green, then by ETA ascending
        const prio = { red: 0, yellow: 1, grey: 2, green: 3 };
        if (prio[a._health] !== prio[b._health]) return prio[a._health] - prio[b._health];
        const aEta = a.estimatedArrival ? new Date(a.estimatedArrival).getTime() : Infinity;
        const bEta = b.estimatedArrival ? new Date(b.estimatedArrival).getTime() : Infinity;
        return aEta - bEta;
      });
  }, [orders, vehicles]);

  // "Urgent next 2h" strip — OTs with deliveryWindowTo within 2h and not delivered
  const urgentSoon = useMemo(() => {
    const now = Date.now();
    const window2h = now + 2 * 3600 * 1000;
    return activeOrders.filter(o => {
      if (!o.deliveryWindowTo) return false;
      const t = new Date(o.deliveryWindowTo).getTime();
      return t > now - 30 * 60000 && t < window2h;
    });
  }, [activeOrders]);

  // Health counters
  const counters = useMemo(() => {
    const c = { red: 0, yellow: 0, grey: 0, green: 0 };
    activeOrders.forEach(o => { c[o._health]++; });
    return c;
  }, [activeOrders]);

  const selectedOrder = activeOrders.find(o => (o.id || o._id) === selectedId) || null;
  const selectedVehicle = selectedOrder?._vehicle;

  const onLoadMap = useCallback((map) => { mapRef.current = map; }, []);

  // Fit bounds to all active vehicles on first load
  useEffect(() => {
    if (!isLoaded || !mapRef.current || activeOrders.length === 0) return;
    const bounds = new window.google.maps.LatLngBounds();
    let any = false;
    activeOrders.forEach(o => {
      if (o._vehicle?.position) {
        bounds.extend({ lat: o._vehicle.position.latitude, lng: o._vehicle.position.longitude });
        any = true;
      }
    });
    if (any) mapRef.current.fitBounds(bounds, { top: 80, bottom: 40, left: 40, right: 40 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, activeOrders.length]);

  // Focus map on selected vehicle
  useEffect(() => {
    if (!mapRef.current || !selectedVehicle?.position) return;
    mapRef.current.panTo({ lat: selectedVehicle.position.latitude, lng: selectedVehicle.position.longitude });
    mapRef.current.setZoom(14);
  }, [selectedVehicle?.position?.latitude, selectedVehicle?.position?.longitude]);

  function handleCardClick(order) {
    const id = order.id || order._id;
    setSelectedId(id === selectedId ? null : id);
    if (onSelectOrder) onSelectOrder(order);
  }

  function handleRecenter() {
    if (!mapRef.current || activeOrders.length === 0) return;
    const bounds = new window.google.maps.LatLngBounds();
    activeOrders.forEach(o => {
      if (o._vehicle?.position) bounds.extend({ lat: o._vehicle.position.latitude, lng: o._vehicle.position.longitude });
    });
    if (!bounds.isEmpty()) mapRef.current.fitBounds(bounds, { top: 80, bottom: 40, left: 40, right: 40 });
  }

  return (
    <Box className={classes.root}>
      {/* Toolbar — counters + legend */}
      <Box className={classes.toolbar}>
        <Typography className={classes.toolbarLabel}>
          {lang === 'pt' ? 'Operação Live' : 'Live Operations'}
        </Typography>
        <Tooltip title={lang === 'pt' ? 'O que é a Vista Operação Live?' : 'What is the Live Operations view?'}>
          <IconButton
            size="small"
            onClick={() => setHelpOpen(true)}
            style={{
              padding: 4, color: '#6366f1',
              background: 'rgba(99,102,241,0.08)',
              border: '1px solid rgba(99,102,241,0.2)',
            }}
          >
            <HelpOutlineIcon style={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
        <Box style={{ flex: 1 }} />
        <Box className={classes.legend}>
          <Box className={classes.legendItem}><span style={{ width: 10, height: 10, borderRadius: '50%', background: '#22c55e' }} />{lang === 'pt' ? 'No prazo' : 'On time'} ({counters.green})</Box>
          <Box className={classes.legendItem}><span style={{ width: 10, height: 10, borderRadius: '50%', background: '#f59e0b' }} />{lang === 'pt' ? 'Em risco' : 'At risk'} ({counters.yellow})</Box>
          <Box className={classes.legendItem}><span style={{ width: 10, height: 10, borderRadius: '50%', background: '#ef4444' }} />{lang === 'pt' ? 'Atrasadas' : 'Delayed'} ({counters.red})</Box>
          <Box className={classes.legendItem}><span style={{ width: 10, height: 10, borderRadius: '50%', background: '#94a3b8' }} />{lang === 'pt' ? 'Sem dados' : 'No data'} ({counters.grey})</Box>
        </Box>
      </Box>

      {/* Help modal — explains the Live Operations view */}
      {helpOpen && (
        <Box style={{
          position: 'fixed', inset: 0,
          background: 'radial-gradient(ellipse at center, rgba(15,23,42,0.7), rgba(15,23,42,0.92))',
          backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
          zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
        }} onClick={() => setHelpOpen(false)}>
          <Box onClick={(e) => e.stopPropagation()} style={{
            background: '#fff', borderRadius: 20,
            width: 680, maxWidth: '96vw', maxHeight: '92vh',
            boxShadow: '0 40px 100px rgba(0,0,0,0.5), 0 0 140px rgba(99,102,241,0.15)',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}>
            {/* Header */}
            <Box style={{
              padding: '20px 24px',
              background: 'linear-gradient(135deg, #1e40af 0%, #6366f1 60%, #8b5cf6 100%)',
              color: '#fff',
              display: 'flex', alignItems: 'center', gap: 14,
              position: 'relative',
            }}>
              <Box style={{
                width: 44, height: 44, borderRadius: 12,
                background: 'rgba(255,255,255,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 22,
                border: '1px solid rgba(255,255,255,0.3)',
                backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
              }}>
                🛰
              </Box>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Typography style={{ fontSize: '1.1rem', fontWeight: 800, color: '#fff', letterSpacing: '-0.01em' }}>
                  {lang === 'pt' ? 'Vista Operação Live' : 'Live Operations View'}
                </Typography>
                <Typography style={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.85)', marginTop: 2 }}>
                  {lang === 'pt' ? 'A sua torre de controlo para operações em curso' : 'Your control tower for operations in progress'}
                </Typography>
              </Box>
              <IconButton
                onClick={() => setHelpOpen(false)}
                style={{
                  color: '#fff',
                  background: 'rgba(255,255,255,0.15)',
                  width: 34, height: 34,
                }}
              >
                <CloseIcon style={{ fontSize: 18 }} />
              </IconButton>
            </Box>

            {/* Body — scrollable */}
            <Box style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>

              {/* Intro */}
              <Typography style={{ fontSize: '0.86rem', color: '#334155', lineHeight: 1.55, marginBottom: 18 }}>
                {lang === 'pt'
                  ? 'Esta vista é pensada para o despachante supervisionar em tempo real todas as ordens activas no terreno. Ao contrário da tabela (gestão) ou do Gantt (planeamento), a Operação Live é uma vista reactiva — destaca imediatamente o que precisa de atenção agora.'
                  : 'This view is designed for the dispatcher to supervise in real time all active orders in the field. Unlike the table (management) or Gantt (planning), Live Operations is reactive — it immediately highlights what needs attention right now.'}
              </Typography>

              {/* What appears here */}
              <Box style={{ marginBottom: 18 }}>
                <Typography style={{ fontSize: '0.58rem', fontWeight: 800, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                  {lang === 'pt' ? 'O que aparece aqui' : 'What appears here'}
                </Typography>
                <Typography style={{ fontSize: '0.78rem', color: '#475569', lineHeight: 1.5 }}>
                  {lang === 'pt'
                    ? 'Apenas OTs com estado Em trânsito ou Atribuída com veículo. OTs pendentes, entregues ou canceladas ficam de fora — só contam as operações activas.'
                    : 'Only orders that are In transit or Assigned with a vehicle. Pending, delivered or cancelled orders are excluded — only active operations count.'}
                </Typography>
              </Box>

              {/* Semáforo de saúde */}
              <Box style={{ marginBottom: 18 }}>
                <Typography style={{ fontSize: '0.58rem', fontWeight: 800, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                  {lang === 'pt' ? 'Semáforo de saúde' : 'Health semaphore'}
                </Typography>
                <Typography style={{ fontSize: '0.76rem', color: '#475569', lineHeight: 1.5, marginBottom: 10 }}>
                  {lang === 'pt'
                    ? 'Cada OT é avaliada automaticamente comparando a hora prevista de chegada (ETA) com a janela de entrega:'
                    : 'Each order is automatically evaluated by comparing the estimated arrival (ETA) with the delivery window:'}
                </Typography>
                <Box style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {[
                    { color: '#22c55e', bg: 'rgba(34,197,94,0.08)', label: lang === 'pt' ? 'No prazo' : 'On time',
                      desc: lang === 'pt' ? 'ETA ≥ 30 min antes do fim da janela — tudo controlado.' : 'ETA ≥ 30 min before window end — everything under control.' },
                    { color: '#f59e0b', bg: 'rgba(245,158,11,0.08)', label: lang === 'pt' ? 'Em risco' : 'At risk',
                      desc: lang === 'pt' ? 'ETA dentro dos 30 min antes ou até 10 min após o fim da janela.' : 'ETA within 30 min before or up to 10 min after window end.' },
                    { color: '#ef4444', bg: 'rgba(239,68,68,0.08)', label: lang === 'pt' ? 'Atrasada' : 'Delayed',
                      desc: lang === 'pt' ? 'ETA ultrapassa a janela em mais de 10 min — agir agora.' : 'ETA exceeds window by more than 10 min — act now.' },
                    { color: '#94a3b8', bg: 'rgba(148,163,184,0.1)', label: lang === 'pt' ? 'Sem dados' : 'No data',
                      desc: lang === 'pt' ? 'Falta ETA ou janela — não é possível avaliar.' : 'ETA or window missing — cannot be evaluated.' },
                  ].map(item => (
                    <Box key={item.label} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '8px 12px',
                      background: item.bg,
                      border: `1px solid ${item.color}30`,
                      borderRadius: 10,
                    }}>
                      <Box style={{
                        width: 12, height: 12, borderRadius: '50%',
                        background: item.color,
                        boxShadow: `0 0 0 2px #fff, 0 0 0 3px ${item.color}40`,
                        flexShrink: 0,
                      }} />
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Typography style={{ fontSize: '0.76rem', fontWeight: 800, color: item.color }}>
                          {item.label}
                        </Typography>
                        <Typography style={{ fontSize: '0.68rem', color: '#64748b', marginTop: 1 }}>
                          {item.desc}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Layout */}
              <Box style={{ marginBottom: 18 }}>
                <Typography style={{ fontSize: '0.58rem', fontWeight: 800, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                  {lang === 'pt' ? 'Como está organizada' : 'Layout'}
                </Typography>
                <Box style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[
                    { n: 1, icon: '⚡', title: lang === 'pt' ? 'Faixa "Próximas 2h"' : '"Next 2h" strip',
                      desc: lang === 'pt' ? 'Só aparece quando há OTs com janela de entrega nas próximas 2 horas. Alerta imediato do que é urgente.' : 'Only shown when there are orders with delivery window in the next 2 hours. Immediate alert for what is urgent.' },
                    { n: 2, icon: '📋', title: lang === 'pt' ? 'Lista à esquerda' : 'Left list',
                      desc: lang === 'pt' ? 'OTs ordenadas por severidade (vermelhas → amarelas → cinzentas → verdes) e depois por ETA. O olho vai direto ao que precisa de atenção.' : 'Orders sorted by severity (red → yellow → grey → green) and then by ETA. Your eye goes straight to what needs attention.' },
                    { n: 3, icon: '🗺', title: lang === 'pt' ? 'Mapa à direita' : 'Right map',
                      desc: lang === 'pt' ? 'Todos os veículos activos em tempo real. Ao clicar numa OT da lista, o mapa faz zoom e centra no veículo correspondente.' : 'All active vehicles in real time. Clicking a card on the left zooms and centers the map on the vehicle.' },
                  ].map(item => (
                    <Box key={item.n} style={{
                      display: 'flex', gap: 10, alignItems: 'flex-start',
                      padding: '10px 12px',
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: 10,
                    }}>
                      <Box style={{
                        width: 30, height: 30, borderRadius: 8,
                        background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 15, flexShrink: 0,
                        boxShadow: '0 2px 6px rgba(99,102,241,0.3)',
                      }}>
                        {item.icon}
                      </Box>
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Typography style={{ fontSize: '0.78rem', fontWeight: 800, color: '#0f172a' }}>
                          {item.title}
                        </Typography>
                        <Typography style={{ fontSize: '0.68rem', color: '#64748b', marginTop: 2, lineHeight: 1.5 }}>
                          {item.desc}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Dica */}
              <Box style={{
                padding: '12px 14px',
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                border: '1px solid #facc15',
                borderRadius: 10,
                display: 'flex', alignItems: 'flex-start', gap: 10,
              }}>
                <Box style={{ fontSize: 18, lineHeight: 1 }}>💡</Box>
                <Box style={{ flex: 1 }}>
                  <Typography style={{ fontSize: '0.68rem', fontWeight: 800, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>
                    {lang === 'pt' ? 'Dica' : 'Tip'}
                  </Typography>
                  <Typography style={{ fontSize: '0.74rem', color: '#78350f', lineHeight: 1.5 }}>
                    {lang === 'pt'
                      ? 'Use esta vista durante o turno de operação para reagir a atrasos enquanto ainda há tempo. Para gerir/editar OTs, troca para a vista em tabela. Para planear o turno, usa o Gantt.'
                      : 'Use this view during the operation shift to react to delays while there is still time. Switch to table view to manage/edit orders. Use Gantt to plan the shift.'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Footer */}
            <Box style={{
              padding: '12px 24px',
              borderTop: '1px solid #e2e8f0',
              background: '#fafbfc',
              display: 'flex', justifyContent: 'flex-end',
            }}>
              <Box
                onClick={() => setHelpOpen(false)}
                style={{
                  padding: '8px 22px', borderRadius: 10, cursor: 'pointer',
                  background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                  color: '#fff', fontSize: '0.78rem', fontWeight: 700,
                  boxShadow: '0 4px 12px rgba(99,102,241,0.35)',
                  textTransform: 'none', userSelect: 'none',
                }}
              >
                {lang === 'pt' ? 'Entendi' : 'Got it'}
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* Urgent-next-2h strip (only shown when non-empty) */}
      {urgentSoon.length > 0 && (
        <Box className={classes.urgentStrip}>
          <Typography style={{ fontSize: '0.58rem', fontWeight: 800, color: '#b91c1c', textTransform: 'uppercase', letterSpacing: '0.05em', alignSelf: 'center', marginRight: 4, flexShrink: 0 }}>
            ⚡ {lang === 'pt' ? 'Próximas 2h' : 'Next 2h'}
          </Typography>
          {urgentSoon.map(o => {
            const cfg = HEALTH_CFG[o._health];
            return (
              <Box key={o.id || o._id} className={classes.urgentChip}
                onClick={() => handleCardClick(o)}
                style={{ borderColor: cfg.color }}>
                <span className={classes.healthDot} style={{ background: cfg.color }} />
                <Box style={{ minWidth: 0 }}>
                  <Typography style={{ fontSize: '0.72rem', fontWeight: 800, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {o.vehiclePlate || '—'} · {otLabel(o)}
                  </Typography>
                  <Typography style={{ fontSize: '0.58rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    → {(o.deliveryName || '').split(',')[0]}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Split body */}
      <Box className={classes.split}>
        {/* Left — OT list sorted by urgency */}
        <Box className={classes.leftPane}>
          <Box className={classes.listHeader}>
            <Typography className={classes.toolbarLabel}>
              {lang === 'pt' ? `${activeOrders.length} OTs activas` : `${activeOrders.length} active OTs`}
            </Typography>
            <Box style={{ flex: 1 }} />
          </Box>
          <Box className={classes.list}>
            {activeOrders.length === 0 ? (
              <Box className={classes.emptyState}>
                <LocalShippingIcon style={{ fontSize: 42, color: '#cbd5e1' }} />
                <Typography style={{ fontSize: '0.82rem', fontWeight: 500 }}>
                  {lang === 'pt' ? 'Sem ordens activas — não há rotas atribuídas, em trânsito ou entregues nas últimas 24h' : 'No active orders — no assigned, in-transit or recently delivered routes'}
                </Typography>
              </Box>
            ) : activeOrders.map(o => {
              const oid = o.id || o._id;
              const cfg = HEALTH_CFG[o._health];
              const HealthIcon = cfg.icon;
              const isSelected = selectedId === oid;
              const v = o._vehicle;
              const speed = v?.position?.speed ? Math.round(v.position.speed * 1.852) : 0;
              return (
                <Box key={oid}
                  className={`${classes.card} ${isSelected ? classes.cardSelected : ''}`}
                  onClick={() => handleCardClick(o)}>
                  <Box className={classes.cardHeader}>
                    <span className={classes.healthDot} style={{ background: cfg.color }} />
                    <Typography className={classes.plate}>{o.vehiclePlate || '—'}</Typography>
                    <Tooltip title={otTooltip(o)}><Typography className={classes.orderNum}>· {otLabel(o)}</Typography></Tooltip>
                    <Box style={{ flex: 1 }} />
                    <Chip size="small"
                      icon={<HealthIcon style={{ fontSize: 10, color: cfg.color }} />}
                      label={cfg.label}
                      style={{
                        height: 18, fontSize: '0.55rem', fontWeight: 800,
                        background: `${cfg.color}15`, color: cfg.color,
                        border: `1px solid ${cfg.color}40`,
                      }} />
                  </Box>
                  {(o.driverName) && (
                    <Box className={classes.cardRow}>
                      <PersonIcon style={{ fontSize: 11, color: '#8b5cf6', flexShrink: 0 }} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.driverName}</span>
                      {v?.position && (
                        <Box style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 3 }}>
                          <SpeedIcon style={{ fontSize: 10, color: '#64748b' }} />
                          <span style={{ fontWeight: 700, color: '#64748b' }}>{speed} km/h</span>
                        </Box>
                      )}
                    </Box>
                  )}
                  <Box className={classes.cardRow}>
                    <FlagIcon style={{ fontSize: 11, color: '#ef4444', flexShrink: 0 }} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {(o.deliveryName || o.deliveryAddress || '').split(',')[0]}
                    </span>
                  </Box>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5 }}>
                    {o.estimatedArrival && (
                      <Tooltip title={lang === 'pt' ? 'Hora de chegada prevista' : 'Estimated arrival'}>
                        <Box className={classes.etaBadge} style={{
                          background: `${cfg.color}18`, color: cfg.color,
                        }}>
                          <AccessTimeIcon style={{ fontSize: 10 }} />
                          {new Date(o.estimatedArrival).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}
                        </Box>
                      </Tooltip>
                    )}
                    {o._delayMin != null && o._delayMin > 5 && (
                      <Box className={classes.etaBadge} style={{ background: '#fef2f2', color: '#dc2626' }}>
                        +{formatTimeDiffMin(o._delayMin)}
                      </Box>
                    )}
                    {o._delayMin != null && o._delayMin < -15 && (
                      <Box className={classes.etaBadge} style={{ background: '#ecfdf5', color: '#16a34a' }}>
                        -{formatTimeDiffMin(o._delayMin)}
                      </Box>
                    )}
                    <Box style={{ flex: 1 }} />
                    {o.driverName && onContactDriver && (
                      <Tooltip title={lang === 'pt' ? 'Contactar motorista' : 'Contact driver'}>
                        <IconButton size="small"
                          onClick={(e) => { e.stopPropagation(); onContactDriver(o); }}
                          style={{ padding: 3 }}>
                          <ChatBubbleIcon style={{ fontSize: 12, color: '#25D366' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Right — live map */}
        <Box className={classes.rightPane}>
          {!isLoaded ? (
            <Box className={classes.emptyState}>
              <CircularProgress size={28} style={{ color: '#6366f1' }} />
            </Box>
          ) : (
            <>
              <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={{ lat: 39.5, lng: -8.0 }}
                zoom={7}
                options={MAP_OPTIONS}
                onLoad={onLoadMap}
              >
                {/* All active vehicles as markers */}
                {activeOrders.map(o => {
                  if (!o._vehicle?.position) return null;
                  const pos = { lat: o._vehicle.position.latitude, lng: o._vehicle.position.longitude };
                  const isSel = (o.id || o._id) === selectedId;
                  const cfg = HEALTH_CFG[o._health];
                  return (
                    <React.Fragment key={`live-${o.id || o._id}`}>
                      {isSel && (
                        <Circle
                          center={pos}
                          radius={60}
                          options={{
                            fillColor: cfg.color, fillOpacity: 0.18,
                            strokeColor: cfg.color, strokeOpacity: 0.55, strokeWeight: 2,
                            clickable: false,
                          }}
                        />
                      )}
                      <Marker
                        position={pos}
                        title={`${o.vehiclePlate || '—'} — ${otLabel(o)}`}
                        icon={{
                          path: window.google.maps.SymbolPath.CIRCLE,
                          scale: isSel ? 10 : 7,
                          fillColor: cfg.color,
                          fillOpacity: isSel ? 1 : 0.85,
                          strokeColor: '#fff',
                          strokeWeight: isSel ? 3 : 2,
                        }}
                        onClick={() => setSelectedId(o.id || o._id)}
                      />
                      {/* Destination pin when selected */}
                      {isSel && o.deliveryLat && o.deliveryLng && (
                        <>
                          <Marker
                            position={{ lat: Number(o.deliveryLat), lng: Number(o.deliveryLng) }}
                            icon={{
                              path: window.google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
                              scale: 6, fillColor: '#ef4444', fillOpacity: 1,
                              strokeColor: '#fff', strokeWeight: 2,
                            }}
                            title={o.deliveryName || 'Entrega'}
                          />
                          <Polyline
                            path={[pos, { lat: Number(o.deliveryLat), lng: Number(o.deliveryLng) }]}
                            options={{
                              strokeColor: cfg.color, strokeOpacity: 0.6, strokeWeight: 3,
                              geodesic: true, clickable: false,
                              icons: [{
                                icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 3 },
                                offset: '0', repeat: '14px',
                              }],
                            }}
                          />
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </GoogleMap>

              {/* Overlay info bar — current selection or global summary */}
              <Box className={classes.mapInfoBar}>
                <LocalShippingIcon style={{ fontSize: 16, color: '#fff' }} />
                {selectedOrder ? (
                  <>
                    <Typography style={{ fontSize: '0.86rem', fontWeight: 800, color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                      {selectedOrder.vehiclePlate}
                    </Typography>
                    <Tooltip title={otTooltip(selectedOrder)} placement="bottom">
                      <Typography style={{ fontSize: '0.66rem', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>
                        {otLabel(selectedOrder)}
                      </Typography>
                    </Tooltip>
                    {selectedOrder.driverName && (
                      <Chip size="small" label={selectedOrder.driverName} style={{ height: 20, fontSize: '0.6rem', background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }} />
                    )}
                    {selectedOrder.estimatedArrival && (
                      <Chip size="small" label={`${lang === 'pt' ? 'Previsto' : 'ETA'} ${new Date(selectedOrder.estimatedArrival).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}`}
                        style={{ height: 20, fontSize: '0.6rem', background: `${HEALTH_CFG[selectedOrder._health].color}66`, color: '#fff', border: `1px solid ${HEALTH_CFG[selectedOrder._health].color}` }} />
                    )}
                  </>
                ) : (
                  <Typography style={{ fontSize: '0.78rem', fontWeight: 700, color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                    {activeOrders.length} {lang === 'pt' ? 'veículos activos' : 'active vehicles'}
                  </Typography>
                )}
                <Box style={{ flex: 1 }} />
                <Tooltip title={lang === 'pt' ? 'Enquadrar todos' : 'Fit all'}>
                  <IconButton size="small" onClick={handleRecenter} style={{ color: '#fff', pointerEvents: 'auto' }}>
                    <CenterFocusStrongIcon style={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default OrdersLiveView;
